import { useLocation, useNavigate } from "react-router-dom";
import useUser, { getAuthorized, isUserDoctor, postAuthorized, putAuthorized } from "../../../hooks/User";
import SEO from "../../../components/SEO";
import ProfileNavigation from "../../../components/common/ProfileNavigation";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import ErrorHandler from "../../../components/common/ErrorHandler";
import { ROUTES } from "../../../routes";
import { MiniTutorial, QUESTION_EDITOR_STEPS } from "../../../components/MiniTutorial";

import "./questioneditor.css";
import config from "../../../Config";

const PopUpState = Object.freeze({
    None: 0,
    Image: 1,
    Audio: 2
});

const ImageExtension = /(\.jpeg|\.jpg|\.png)$/i;
const AudioExtension = /(\.wav|\.ogg|\.mp3)$/i;

const QuestionContext = createContext();

function QuestionContextProvider({ questionid, children }) {
    const [id, setId] = useState();
    const [text, setText] = useState("");
    const [isPublic, setIsPublic] = useState(false);
    const [answerOptions, setAnswerOptions] = useState([]);
    const [voiceFile, setVoiceFile] = useState(null);
    const [voiceFilename, setVoiceFilename] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imageFilename, setImageFilename] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (questionid === null || questionid === undefined) {
            // Creating new question - reset all fields
            setId(undefined);
            setText("");
            setIsPublic(false);
            setAnswerOptions([]);
            setVoiceFilename(null);
            setImageFilename(null);
            setVoiceFile(null);
            setImageFile(null);
            return;
        }

        setLoading(true);
        getAuthorized("/doctor/get_question", {
            params: { question_id: questionid }
        })
            .then((response) => {
                const quest = response.data.question;
                setId(quest.id);
                setText(quest.text);
                setIsPublic(quest.is_public);
                setAnswerOptions(quest.answer_options || []);
                setVoiceFilename(quest.voice_filename);
                setImageFilename(quest.picture_filename);
            })
            .catch((error) => {
                console.error("Failed to load question:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [questionid]);

    return (
        <QuestionContext.Provider
            value={{
                id,
                text,
                setText,
                isPublic,
                setIsPublic,
                answerOptions,
                setAnswerOptions,
                imageFile,
                setImageFile,
                imageFilename,
                setImageFilename,
                voiceFile,
                setVoiceFile,
                voiceFilename,
                setVoiceFilename,
                loading
            }}
        >
            {children}
        </QuestionContext.Provider>
    );
}

// Media Upload Popup Component
function MediaUploadPopup({ type, isOpen, onClose }) {
    const { setErrorList } = useContext(ErrorHandlerContext);
    const {
        imageFile,
        setImageFile,
        imageFilename,
        voiceFile,
        setVoiceFile,
        voiceFilename
    } = useContext(QuestionContext);

    const fileInputRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const isImage = type === "image";
    const currentFile = isImage ? imageFile : voiceFile;
    const currentFilename = isImage ? imageFilename : voiceFilename;
    const setCurrentFile = isImage ? setImageFile : setVoiceFile;
    const acceptedTypes = isImage ? "image/*" : "audio/*";
    const title = isImage ? "Изображение вопроса" : "Голосовое сообщение";
    const icon = isImage ? "🖼️" : "🎵";
    const hint = isImage ? "JPEG, PNG (макс. 5MB)" : "MP3, WAV, OGG (макс. 10MB)";

    useEffect(() => {
        if (!isOpen) {
            setPreviewUrl(null);
            return;
        }

        if (currentFile) {
            const reader = new FileReader();
            reader.onload = (e) => setPreviewUrl(e.target.result);
            reader.readAsDataURL(currentFile);
        } else if (currentFilename) {
            setPreviewUrl(config.getMediaUrl(currentFilename));
        }
    }, [isOpen, currentFile, currentFilename]);

    const handleFileSelect = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback(
        (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            const extension = isImage ? ImageExtension : AudioExtension;
            if (!extension.exec(file.name)) {
                setErrorList((prev) => [
                    ...prev,
                    `Неверный формат файла. Разрешены: ${hint}`
                ]);
                return;
            }

            setCurrentFile(file);
        },
        [isImage, hint, setCurrentFile, setErrorList]
    );

    const handleClear = useCallback(() => {
        setCurrentFile(null);
        setPreviewUrl(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, [setCurrentFile]);

    if (!isOpen) return null;

    return (
        <div className="popup-overlay" onClick={onClose}>
            <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <div className="popup-header">
                    <h3>{title}</h3>
                    <button
                        className="button-secondary"
                        onClick={onClose}
                        style={{ padding: "8px 12px" }}
                    >
                        ✕
                    </button>
                </div>

                <div className="popup-body">
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept={acceptedTypes}
                        onChange={handleFileChange}
                        hidden
                    />

                    {!previewUrl ? (
                        <div className="file-upload-zone" onClick={handleFileSelect}>
                            <div className="file-upload-icon">{icon}</div>
                            <div className="file-upload-text">Нажмите для загрузки</div>
                            <div className="file-upload-hint">{hint}</div>
                        </div>
                    ) : (
                        <>
                            <div className="file-upload-zone has-file">
                                <div className="file-preview">
                                    {isImage ? (
                                        <img src={previewUrl} alt="Preview" />
                                    ) : (
                                        <audio controls src={previewUrl} />
                                    )}
                                </div>
                            </div>
                            <div style={{ textAlign: "center", marginTop: "16px" }}>
                                <button
                                    className="button-secondary"
                                    onClick={handleFileSelect}
                                >
                                    🔄 Заменить файл
                                </button>
                            </div>
                        </>
                    )}
                </div>

                <div className="popup-footer">
                    {previewUrl && (
                        <button className="button-danger" onClick={handleClear}>
                            Удалить
                        </button>
                    )}
                    <button onClick={onClose}>Готово</button>
                </div>
            </div>
        </div>
    );
}

// Text Editor Component
function TextEditor() {
    const { text, setText } = useContext(QuestionContext);
    const [error, setError] = useState("");

    const handleChange = useCallback(
        (e) => {
            const value = e.target.value;
            setText(value);
            if (value.trim().length > 0) {
                setError("");
            }
        },
        [setText]
    );

    return (
        <div className="form-section">
            <label className="form-section-label">Текст вопроса *</label>
            <textarea
                className={`question-textarea ${error ? "input-error" : ""}`}
                value={text}
                onChange={handleChange}
                placeholder="Введите текст вопроса..."
                rows={4}
            />
            {error && <div className="field-error">⚠️ {error}</div>}
        </div>
    );
}

// Status Toggle Component
function StatusToggle() {
    const { isPublic, setIsPublic } = useContext(QuestionContext);

    return (
        <div className="form-section">
            <label className="form-section-label">Доступность</label>
            <div className="status-toggle">
                <button
                    className={`status-toggle-option ${!isPublic ? "active" : ""}`}
                    onClick={() => setIsPublic(false)}
                    type="button"
                >
                    🔒 Приватный
                </button>
                <button
                    className={`status-toggle-option ${isPublic ? "active" : ""}`}
                    onClick={() => setIsPublic(true)}
                    type="button"
                >
                    🌍 Публичный
                </button>
            </div>
        </div>
    );
}

// Answer Option Item Component
function AnswerOptionItem({ index, value }) {
    const { setAnswerOptions } = useContext(QuestionContext);

    const handleChange = useCallback(
        (e) => {
            const newValue = e.target.value;
            setAnswerOptions((prev) => {
                const result = [...prev];
                result[index] = newValue;
                return result;
            });
        },
        [index, setAnswerOptions]
    );

    const handleDelete = useCallback(() => {
        setAnswerOptions((prev) => prev.filter((_, i) => i !== index));
    }, [index, setAnswerOptions]);

    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                // Add new option on Enter
                setAnswerOptions((prev) => [...prev, ""]);
                // Focus next input after render
                setTimeout(() => {
                    const inputs = document.querySelectorAll(".answer-option-input");
                    if (inputs[index + 1]) {
                        inputs[index + 1].focus();
                    }
                }, 0);
            }
        },
        [index, setAnswerOptions]
    );

    return (
        <div className="answer-option-item">
            <span className="answer-option-number">{index + 1}</span>
            <input
                type="text"
                className="answer-option-input"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={`Вариант ответа ${index + 1}`}
            />
            <button
                className="answer-option-delete"
                onClick={handleDelete}
                title="Удалить вариант"
                type="button"
            >
                🗑️
            </button>
        </div>
    );
}

// Answer Options Editor Component
function AnswerOptionsEditor() {
    const { answerOptions, setAnswerOptions } = useContext(QuestionContext);

    const handleAdd = useCallback(() => {
        setAnswerOptions((prev) => [...prev, ""]);
    }, [setAnswerOptions]);

    return (
        <div className="form-section">
            <label className="form-section-label">
                Варианты ответа ({answerOptions.length})
            </label>
            {answerOptions.length === 0 ? (
                <div className="empty-options-state">
                    <div className="empty-options-state-icon">📝</div>
                    <div>Добавьте варианты ответа</div>
                </div>
            ) : (
                <div className="answer-options-list">
                    {answerOptions.map((value, index) => (
                        <AnswerOptionItem key={index} index={index} value={value} />
                    ))}
                </div>
            )}
            <button className="add-option-button" onClick={handleAdd} type="button">
                <span className="add-option-icon">+</span>
                <span>Добавить вариант</span>
            </button>
        </div>
    );
}

// Media Upload Cards Component
function MediaUploadCards({ onImageClick, onAudioClick }) {
    const { imageFilename, voiceFilename } = useContext(QuestionContext);

    return (
        <div className="form-section">
            <label className="form-section-label">Медиа материалы</label>
            <div className="media-upload-grid">
                <div
                    className={`media-upload-card ${imageFilename ? "has-content" : ""}`}
                    onClick={onImageClick}
                >
                    <div className="media-upload-icon">
                        {imageFilename ? "🖼️" : "📷"}
                    </div>
                    <div className="media-upload-label">
                        {imageFilename ? "Изображение загружено" : "Добавить изображение"}
                    </div>
                    <div className="media-upload-hint">
                        {imageFilename ? "Нажмите для изменения" : "JPEG, PNG"}
                    </div>
                </div>
                <div
                    className={`media-upload-card ${voiceFilename ? "has-content" : ""}`}
                    onClick={onAudioClick}
                >
                    <div className="media-upload-icon">
                        {voiceFilename ? "🎵" : "🎤"}
                    </div>
                    <div className="media-upload-label">
                        {voiceFilename ? "Аудио загружено" : "Добавить голос"}
                    </div>
                    <div className="media-upload-hint">
                        {voiceFilename ? "Нажмите для изменения" : "MP3, WAV, OGG"}
                    </div>
                </div>
            </div>
        </div>
    );
}

// File Upload Utilities
async function uploadFile(file) {
    const form = new FormData();
    form.append("file", file);
    const response = await postAuthorized("/client/upload_file", form, {});
    return response.data.filename;
}

async function uploadImageAndAudio(image, audio) {
    const results = {
        image: null,
        audio: null
    };

    if (image) results.image = await uploadFile(image);
    if (audio) results.audio = await uploadFile(audio);

    return results;
}

async function updateQuestion({
    id,
    text,
    answerOptions,
    isPublic,
    voiceFilename,
    imageFilename
}) {
    const params = {
        question_id: id,
        text: text,
        is_public: isPublic,
        answer_options: answerOptions
    };

    if (voiceFilename) params.voice_filename = voiceFilename;
    if (imageFilename) params.picture_filename = imageFilename;

    return putAuthorized("/doctor/update_question", {}, { params });
}

async function createQuestion({
    text,
    answerOptions,
    isPublic,
    voiceFilename,
    imageFilename
}) {
    const form = new FormData();
    form.append("text", text);
    form.append("is_public", isPublic);

    for (let value of answerOptions) form.append("answer_options", value);

    if (voiceFilename) form.append("voice_filename", voiceFilename);
    if (imageFilename) form.append("picture_filename", imageFilename);

    return postAuthorized("/doctor/add_question", form, {});
}

// Main Question Editor Component
function QuestionEditor() {
    const [currentPopUp, setCurrentPopUp] = useState(PopUpState.None);
    const { setErrorList } = useContext(ErrorHandlerContext);
    const {
        id,
        text,
        isPublic,
        answerOptions,
        voiceFile,
        imageFile,
        voiceFilename,
        imageFilename,
        setImageFile,
        setVoiceFile,
        loading
    } = useContext(QuestionContext);
    const nav = useNavigate();
    const [isSaving, setIsSaving] = useState(false);

    const validateForm = useCallback(() => {
        const errors = [];

        if (!text || text.trim().length === 0) {
            errors.push("Введите текст вопроса");
        }

        if (answerOptions.length === 0) {
            errors.push("Добавьте хотя бы один вариант ответа");
        } else {
            const emptyOptions = answerOptions.filter((opt) => !opt || opt.trim() === "");
            if (emptyOptions.length > 0) {
                errors.push("Заполните все варианты ответа");
            }
        }

        return errors;
    }, [text, answerOptions]);

    const handleSave = useCallback(async () => {
        const errors = validateForm();
        if (errors.length > 0) {
            setErrorList((prev) => [...prev, ...errors]);
            return;
        }

        setIsSaving(true);
        try {
            const results = await uploadImageAndAudio(imageFile, voiceFile);

            const questionObj = {
                text,
                answerOptions,
                isPublic,
                voiceFilename: results.audio || voiceFilename,
                imageFilename: results.image || imageFilename
            };

            if (id) {
                questionObj.id = id;
                await updateQuestion(questionObj);
            } else {
                await createQuestion(questionObj);
            }

            nav(ROUTES.QUESTIONS, { viewTransition: true });
        } catch (error) {
            console.error("Save error:", error);
            setErrorList((prev) => [
                ...prev,
                error.response?.data?.error_description || "Ошибка при сохранении вопроса"
            ]);
        } finally {
            setIsSaving(false);
        }
    }, [
        id,
        text,
        answerOptions,
        isPublic,
        voiceFile,
        imageFile,
        voiceFilename,
        imageFilename,
        validateForm,
        setErrorList,
        nav
    ]);

    const handleBack = useCallback(() => {
        nav(ROUTES.QUESTIONS, { viewTransition: true });
    }, [nav]);

    const handleClearMedia = useCallback(() => {
        setImageFile(null);
        setVoiceFile(null);
    }, [setImageFile, setVoiceFile]);

    if (loading) {
        return (
            <div className="center-h" style={{ padding: "60px" }}>
                <div className="spinner"></div>
                <p style={{ marginTop: "20px", color: "var(--text-muted)" }}>
                    Загрузка вопроса...
                </p>
            </div>
        );
    }

    const isEditing = id !== undefined;

    return (
        <div className="question-editor-container">
            <div className="question-editor-card" style={{ position: "relative" }}>
                {isSaving && (
                    <div className="loading-overlay">
                        <div className="spinner"></div>
                    </div>
                )}

                <h1>{isEditing ? "✏️ Редактирование вопроса" : "➕ Новый вопрос"}</h1>

                <TextEditor />
                <StatusToggle />
                <AnswerOptionsEditor />
                <MediaUploadCards
                    onImageClick={() => setCurrentPopUp(PopUpState.Image)}
                    onAudioClick={() => setCurrentPopUp(PopUpState.Audio)}
                />

                <div className="editor-actions">
                    <button
                        className="button-secondary"
                        onClick={handleBack}
                        type="button"
                    >
                        ← Назад
                    </button>
                    <div className="editor-actions-right">
                        {(imageFile || voiceFile) && (
                            <button
                                className="button-secondary"
                                onClick={handleClearMedia}
                                type="button"
                            >
                                Очистить медиа
                            </button>
                        )}
                        <button onClick={handleSave} disabled={isSaving} type="button">
                            {isSaving ? "💾 Сохранение..." : "💾 Сохранить"}
                        </button>
                    </div>
                </div>
            </div>

            <MediaUploadPopup
                type="image"
                isOpen={currentPopUp === PopUpState.Image}
                onClose={() => setCurrentPopUp(PopUpState.None)}
            />
            <MediaUploadPopup
                type="audio"
                isOpen={currentPopUp === PopUpState.Audio}
                onClose={() => setCurrentPopUp(PopUpState.None)}
            />
            <MiniTutorial steps={QUESTION_EDITOR_STEPS} pageKey="question-editor" />
        </div>
    );
}

// Main Page Component
export default function QuestionEditorPage() {
    const { data, loading, error } = useUser();
    const nav = useNavigate();
    const location = useLocation();
    const [questId, setQuestId] = useState();

    useEffect(() => {
        if (loading) return;
        if (error) nav(ROUTES.AUTH, { viewTransition: true });
        if (!isUserDoctor(data)) nav(ROUTES.PROFILE, { viewTransition: true });

        const id = location?.state?.questionId;
        setQuestId(id);
    }, [data, loading, error, nav, location, setQuestId]);

    return (<>
        <SEO title="Редактор вопроса" description="Создание и редактирование вопроса для медицинского опросника" noindex={true} />
        <ErrorHandlerContextProvider>
            <ProfileNavigation isDoctor={isUserDoctor(data)} />
            <QuestionContextProvider questionid={questId}>
                <QuestionEditor />
            </QuestionContextProvider>
            <ErrorHandler />
        </ErrorHandlerContextProvider>
    </>);
}
